import React, { useMemo, useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';

const CONCURRENCY = 3;
const BATCH_DELAY_MS = 1500; // delay between batches to avoid rate limiting
const MAX_RETRIES = 2;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function SearchVolumeBulk() {
  const [input, setInput] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const abortRef = useRef(false);

  const brandId = useMemo(() => {
    try { return localStorage.getItem('currentBrandId') || ''; } catch { return ''; }
  }, []);

  const keywords = useMemo(() => {
    return input.split('\n').map(l => l.trim()).filter(Boolean);
  }, [input]);

  const fetchOne = useCallback(async (keyword) => {
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await fetch('https://api.glowytics.com/api/search-volume/monthly', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
          body: JSON.stringify({ keyword, months: 0, languageConstant: '1037' })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          // Retry on 429 (rate limit) or 5xx errors
          if (attempt < MAX_RETRIES && (res.status === 429 || res.status >= 500)) {
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
            continue;
          }
          return { keyword, monthly: [], error: err?.error || res.statusText };
        }
        const data = await res.json();
        // If script failed but returned JSON, retry
        if (data.success === false && attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        return {
          keyword,
          monthly: Array.isArray(data.monthly) ? data.monthly : [],
          competition: data.competition || null,
          competition_index: data.competition_index ?? null,
          top_of_page_bid_low: data.top_of_page_bid_low ?? null,
          top_of_page_bid_high: data.top_of_page_bid_high ?? null,
        };
      } catch (e) {
        if (attempt < MAX_RETRIES) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        return { keyword, monthly: [], error: e.message };
      }
    }
  }, [brandId]);

  const run = async () => {
    if (keywords.length === 0) return;
    abortRef.current = false;
    setRunning(true);
    setResults([]);
    setProgress({ done: 0, total: keywords.length });

    const allResults = [];
    let idx = 0;

    const runBatch = async () => {
      const batch = [];
      while (batch.length < CONCURRENCY && idx < keywords.length) {
        if (abortRef.current) break;
        batch.push(fetchOne(keywords[idx]));
        idx++;
      }
      const batchResults = await Promise.all(batch);
      allResults.push(...batchResults);
      setResults(prev => [...prev, ...batchResults]);
      setProgress({ done: allResults.length, total: keywords.length });
    };

    while (idx < keywords.length && !abortRef.current) {
      await runBatch();
      // Delay between batches to avoid rate limiting
      if (idx < keywords.length && !abortRef.current) {
        await new Promise(r => setTimeout(r, BATCH_DELAY_MS));
      }
    }

    setRunning(false);
  };

  const stop = () => { abortRef.current = true; };

  // Build month columns from results
  const monthColumns = useMemo(() => {
    const cols = new Set();
    for (const r of results) {
      for (const m of (r.monthly || [])) {
        cols.add(`${m.year}-${String(m.month).padStart(2, '0')}`);
      }
    }
    return Array.from(cols).sort();
  }, [results]);

  // Compute avg monthly for each result
  const getAvg = (r) => {
    if (!r.monthly || r.monthly.length === 0) return 0;
    const sum = r.monthly.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    return Math.round(sum / r.monthly.length);
  };

  const getLast12Total = (r) => {
    if (!r.monthly || r.monthly.length === 0) return 0;
    const sorted = [...r.monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    const last12 = sorted.slice(-12);
    return last12.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
  };

  // 3-month change: compare last 3 months total vs previous 3 months total
  const get3MonthChange = (r) => {
    if (!r.monthly || r.monthly.length < 6) return null;
    const sorted = [...r.monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    const last3 = sorted.slice(-3);
    const prev3 = sorted.slice(-6, -3);
    const sumLast = last3.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    const sumPrev = prev3.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    if (sumPrev === 0) return null;
    return Math.round(((sumLast - sumPrev) / sumPrev) * 100);
  };

  // YoY change: compare last 12 months vs previous 12 months
  const getYoyChange = (r) => {
    if (!r.monthly || r.monthly.length < 24) return null;
    const sorted = [...r.monthly].sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month));
    const last12 = sorted.slice(-12);
    const prev12 = sorted.slice(-24, -12);
    const sumLast = last12.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    const sumPrev = prev12.reduce((s, m) => s + Number(m.monthly_searches || 0), 0);
    if (sumPrev === 0) return null;
    return Math.round(((sumLast - sumPrev) / sumPrev) * 100);
  };

  const formatPct = (val) => {
    if (val === null || val === undefined) return '-';
    const color = val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-600' : 'text-gray-500';
    return <span className={color}>{val > 0 ? '+' : ''}{val}%</span>;
  };

  const competitionColor = (comp) => {
    if (!comp) return '';
    if (comp === 'LOW') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (comp === 'MEDIUM') return 'bg-amber-50 text-amber-700 border-amber-200';
    if (comp === 'HIGH') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    return `${val.toFixed(2)} ₺`;
  };

  const exportExcel = () => {
    const rows = results.map(r => {
      const row = {
        'Keyword': r.keyword,
        'Avg Monthly': getAvg(r),
        'Last 12mo Total': getLast12Total(r),
        '3mo Change %': get3MonthChange(r) ?? '',
        'YoY Change %': getYoyChange(r) ?? '',
        'Competition': r.competition || '',
        'Competition Index': r.competition_index ?? '',
        'Top Page Bid Low': r.top_of_page_bid_low ?? '',
        'Top Page Bid High': r.top_of_page_bid_high ?? '',
        'Status': r.error || 'OK'
      };
      for (const col of monthColumns) {
        const [y, m] = col.split('-').map(Number);
        const found = (r.monthly || []).find(x => x.year === y && x.month === m);
        row[`${MONTH_NAMES[m - 1]} ${y}`] = found ? Number(found.monthly_searches || 0) : '';
      }
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Search Volumes');
    XLSX.writeFile(wb, `search_volumes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">LAB</span>
          <h2 className="text-xl font-bold text-gray-900">Bulk Search Volume</h2>
        </div>
        <p className="text-sm text-gray-500">Paste keywords (one per line), fetch Google search volumes, and export to Excel.</p>
      </div>

      {/* Input */}
      <div className="bg-white rounded-xl border shadow-sm p-5 mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">Keywords ({keywords.length})</label>
          <div className="flex items-center gap-2">
            {results.length > 0 && (
              <button
                onClick={exportExcel}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Export Excel ({results.length})
              </button>
            )}
            {running ? (
              <button
                onClick={stop}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={run}
                disabled={keywords.length === 0}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Fetch Volumes
              </button>
            )}
          </div>
        </div>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          rows={8}
          placeholder={"spor ayakkabı\nkoşu ayakkabısı\nyürüyüş ayakkabısı\n..."}
          className="w-full px-3 py-2 border rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
          disabled={running}
        />
      </div>

      {/* Progress */}
      {(running || progress.done > 0) && (
        <div className="bg-white rounded-xl border shadow-sm p-5 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-700 font-medium">
              Progress: {progress.done} / {progress.total} ({pct}%)
            </span>
            {running && <span className="text-xs text-blue-600 animate-pulse">Fetching...</span>}
            {!running && progress.done > 0 && <span className="text-xs text-emerald-600">Done</span>}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, background: running ? '#3b82f6' : '#10b981' }}
            />
          </div>
          {results.filter(r => r.error).length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              {results.filter(r => r.error).length} failed
            </div>
          )}
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-800">Results ({results.length})</h3>
            <button
              onClick={exportExcel}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Export Excel
            </button>
          </div>
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Keyword</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Avg Monthly</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Last 12mo</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">3mo Chg</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">YoY Chg</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Competition</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid Low</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Bid High</th>
                  <th className="px-3 py-2 text-center font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-1.5 text-gray-400">{i + 1}</td>
                    <td className="px-3 py-1.5 font-medium text-gray-900 max-w-[300px] truncate">{r.keyword}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{getAvg(r).toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{getLast12Total(r).toLocaleString('tr-TR')}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{formatPct(get3MonthChange(r))}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{formatPct(getYoyChange(r))}</td>
                    <td className="px-3 py-1.5 text-center">
                      {r.competition ? (
                        <span className={`px-2 py-0.5 rounded text-[10px] border ${competitionColor(r.competition)}`}>
                          {r.competition}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatCurrency(r.top_of_page_bid_low)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-gray-600">{formatCurrency(r.top_of_page_bid_high)}</td>
                    <td className="px-3 py-1.5 text-center">
                      {r.error ? (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-red-50 text-red-600 border border-red-200" title={r.error}>Error</span>
                      ) : !r.monthly || r.monthly.length === 0 ? (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-amber-50 text-amber-600 border border-amber-200">No data</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
